<template>
	<div class="edit-container">
		<div class="header">
			<h2>Edit Group</h2>
			<button class="close-btn" @click="$emit('close')">✕</button>
		</div>
		
		<form @submit.prevent="saveChanges" class="edit-form">
			<div class="form-group">
				<label for="group-name">Group Name</label>
				<input
						id="group-name"
						v-model="form.name"
						type="text"
						required
						maxlength="255"
						placeholder="e.g., Main Bar TVs"
				>
			</div>
			
			<div class="form-group">
				<label for="group-app">
					<Icon name="app" size="sm"/>
					Default App
				</label>
				<select id="group-app" v-model="form.default_app">
					<option value="none">No Default App</option>
					<option
							v-for="app in availableApps"
							:key="app.key"
							:value="app.key"
					>
						{{ app.label }}
					</option>
				</select>
			</div>
			
			<div class="form-group">
				<label>
					<Icon name="tv" size="sm"/>
					Displays
				</label>
				<div class="member-selection">
					<div class="available-tvs">
						<h4>Available</h4>
						<div class="tv-list">
							<div
									v-for="tv in availableTVs"
									:key="tv.id"
									class="tv-item"
									@click="addTv(tv)"
							>
								<Icon name="tv" size="sm"/>
								<span>{{ tv.name }}</span>
								<span class="tv-code">{{ tv.identifier_code }}</span>
							</div>
						</div>
					</div>
					
					<div class="current-members">
						<h4>Added</h4>
						<div class="tv-list">
							<div
								v-for="tv in currentMembers"
								:key="tv.id"
								class="tv-item member"
								@click="removeTv(tv)"
							>
								<Icon name="tv" size="sm"/>
								<span>{{ tv.name }}</span>
								<span class="tv-code">{{ tv.identifier_code }}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
			
			<div class="form-actions">
				<button type="button" class="btn-secondary" @click="$emit('close')">
					Cancel
				</button>
				<button type="submit" class="btn-primary" :disabled="isSaving">
					{{ isSaving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
			
			<div class="delete-section">
				<button type="button" class="btn-danger" @click="showDeleteConfirm = true">
					<Icon name="trash" size="sm"/>
					Delete Group
				</button>
			</div>
		</form>
		
		<Modal
			:show="showDeleteConfirm"
			title="Delete Group"
			confirm-text="Delete Group"
			confirm-button-class="danger"
			:confirm-disabled="isDeleting"
			@close="showDeleteConfirm = false"
			@cancel="showDeleteConfirm = false"
			@confirm="deleteGroup"
		>
			<div class="delete-confirmation">
				<p>Are you sure you want to delete <strong>{{ props.group.name }}</strong>?</p>
				<p class="warning-text">This action cannot be undone. All devices in this group will be moved to "No
					Group".</p>
			</div>
		</Modal>
	</div>
</template>

<script setup>
import {computed, ref, onMounted} from 'vue'
import {useMainStore} from '../store/main'
import Icon from "./Icon.vue";
import Modal from "./Modal.vue";

const props = defineProps({
	group: {type: Object, required: true}
})

const emit = defineEmits(['close', 'saved', 'deleted'])

const store = useMainStore()
const isSaving = ref(false)
const isDeleting = ref(false)
const showDeleteConfirm = ref(false)

const form = ref({
	name: props.group.name,
	default_app: props.group.default_app?.key || 'none'
})

const availableApps = computed(() => store.apps)

const availableTVs = computed(() => {
	return store.pairedTVs.filter(tv =>
			tv.site_id === store.user.host_site_id &&
			tv.group_id !== props.group.id
	)
})

const currentMembers = computed(() => {
	return props.group.members || []
})

function addTv(tv) {
	// Add to current members
	const members = [...currentMembers.value, tv]
	// Update form
	form.value.members = members
}

function removeTv(tv) {
	// Remove from current members
	const members = currentMembers.value.filter(member => member.id !== tv.id)
	// Update form
	form.value.members = members
}

async function saveChanges() {
	isSaving.value = true
	
	try {
		const response = await fetch(`/api/groups/${props.group.id}`, {
			method: 'PATCH',
			headers: {
				'Authorization': `Bearer ${sessionStorage.getItem('buzztime_token')}`,
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				name: form.value.name,
				default_app: form.value.default_app === 'none' ? null : form.value.default_app
			})
		})
		
		if (response.ok) {
			const updatedGroup = await response.json()
			emit('saved', updatedGroup.group)
			emit('close')
		} else {
			const error = await response.json()
			alert(`Failed to save: ${error.message}`)
		}
	} catch (error) {
		console.error('Save error:', error)
		alert('Failed to save changes')
	} finally {
		isSaving.value = false
	}
}

async function deleteGroup() {
	isDeleting.value = true
	
	try {
		const response = await fetch(`/api/groups/${props.group.id}`, {
			method: 'DELETE',
			headers: {
				'Authorization': `Bearer ${sessionStorage.getItem('buzztime_token')}`
			}
		})
		
		if (response.ok) {
			emit('deleted', props.group)
			emit('close')
		} else {
			const error = await response.json()
			alert(`Failed to delete group: ${error.message}`)
		}
	} catch (error) {
		alert('Failed to delete group')
		console.error('Delete error:', error)
	} finally {
		isDeleting.value = false
		showDeleteConfirm.value = false
	}
}

onMounted(async () => {
	if (store.apps.length === 0) {
		await store.loadApps()
	}
})
</script>

<style scoped>
/* Same styles as DeviceEdit.vue - reuse the styling */
.edit-container {
	background: var(--color-popup-dark);
	border: var(--border-thin) var(--color-border);
	border-radius: var(--border-radius-xl);
	padding: var(--spacing-xl);
	max-width: 500px;
	margin: var(--spacing-lg) auto;
}

.header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: var(--spacing-lg);
}

.header h2 {
	margin: 0;
	color: var(--color-text);
}

.close-btn {
	background: none;
	border: none;
	color: var(--color-text-secondary);
	font-size: 1.2rem;
	cursor: pointer;
	padding: var(--spacing-sm);
	border-radius: var(--border-radius-sm);
}

.close-btn:hover {
	background: var(--color-popup-element);
}

.edit-form {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);
}

.form-group {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
}

.form-group label {
	font-size: var(--font-size-sm);
	color: var(--color-text-secondary);
	font-weight: var(--font-weight-medium);
}

.form-group input,
.form-group select {
	background: var(--color-popup);
	border: var(--border-thin) var(--color-border);
	border-radius: var(--border-radius-md);
	padding: var(--spacing-md);
	color: var(--color-text);
	font-size: var(--font-size-base);
}

.form-group input:focus,
.form-group select:focus {
	outline: none;
	border-color: var(--color-primary);
}

.member-selection {
	display: flex;
	gap: var(--spacing-md);
	min-height: 200px;
}

.available-tvs,
.current-members {
	flex: 1;
	background: var(--color-popup);
	border: var(--border-thin) var(--color-border);
	border-radius: var(--border-radius-md);
	padding: var(--spacing-md);
}

.available-tvs h4,
.current-members h4 {
	margin: 0 0 var(--spacing-sm) 0;
	font-size: var(--font-size-sm);
	color: var(--color-text-secondary);
}

.tv-list {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xs);
	max-height: 150px;
	overflow-y: auto;
}

.tv-item {
	display: flex;
	align-items: center;
	gap: var(--spacing-sm);
	padding: var(--spacing-sm);
	background: var(--color-popup-element);
	border: var(--border-thin) var(--color-border);
	border-radius: var(--border-radius-sm);
	cursor: pointer;
	transition: all var(--transition-fast);
}

.tv-item:hover {
	background: var(--color-popup-light);
}

.tv-item.member {
	background: var(--color-primary-light);
	border-color: var(--color-primary);
}

.tv-code {
	font-size: var(--font-size-xs);
	color: var(--color-subtle);
	margin-left: auto;
}

.form-actions {
	display: flex;
	gap: var(--spacing-md);
	margin-top: var(--spacing-lg);
}

.delete-section {
	margin-top: var(--spacing-lg);
	padding-top: var(--spacing-lg);
	border-top: var(--border-thin) var(--color-border);
}

.delete-confirmation {
	text-align: center;
}

.delete-confirmation p {
	margin: 0 0 var(--spacing-md) 0;
	color: var(--color-text);
}

.warning-text {
	font-size: var(--font-size-sm);
	color: var(--color-text-secondary);
}

.btn-primary,
.btn-secondary {
	flex: 1;
	padding: var(--spacing-md) var(--spacing-lg);
	border: none;
	border-radius: var(--border-radius-md);
	font-size: var(--font-size-sm);
	font-weight: var(--font-weight-medium);
	cursor: pointer;
	transition: all var(--transition-fast);
}

.btn-primary {
	background: var(--color-primary);
	color: white;
}

.btn-primary:hover:not(:disabled) {
	background: var(--color-primary-hover);
}

.btn-secondary {
	background: var(--color-popup-element);
	color: var(--color-text);
}

.btn-secondary:hover {
	background: var(--color-popup-light);
}

.btn-primary:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}
</style>